import type { Document, DocumentUpload } from '@onefootprint/request-types/dashboard';
import { forwardRef } from 'react';
import type { EntityVault } from '@/types/vault';
import type { Rotation } from '@/utils/rotation';
import { getUploadKey } from '../../utils/get-doc-upload-key';
import UploadTitleCard from '../upload-title-card';
import PdfThumbnail from './components/pdf-thumbnail';
import UploadImage from './components/upload-image';
import useUploadData from './hooks/use-upload-data/use-upload-data';

type UploadImageItemProps = {
  document: Document;
  upload: DocumentUpload & { isLatest: boolean };
  vault: EntityVault;
  imageOnly?: boolean;
  rotateIndex?: number;
  rotation?: Rotation;
  onRotate?: () => void;
};

const UploadImageItem = forwardRef<HTMLDivElement, UploadImageItemProps>(
  ({ document, upload, vault, imageOnly, rotateIndex, rotation, onRotate }, ref) => {
    const { objectUrl, isPdf } = useUploadData(document, upload, vault);

    if (!objectUrl) {
      return null;
    }

    if (isPdf) {
      return <PdfThumbnail src={objectUrl} />;
    }

    return (
      <div className="flex flex-col items-center w-full gap-2 scroll-mt-2" ref={ref}>
        {imageOnly ? (
          <UploadImage
            objectUrl={objectUrl}
            alt={getUploadKey(upload)}
            failureReasons={upload.failureReasons}
            rotateIndex={rotateIndex}
            rotation={rotation}
          />
        ) : (
          <>
            <UploadTitleCard upload={upload} onRotate={onRotate} />
            <UploadImage
              objectUrl={objectUrl}
              alt={getUploadKey(upload)}
              failureReasons={upload.failureReasons}
              rotateIndex={rotateIndex}
              rotation={rotation}
            />
          </>
        )}
      </div>
    );
  },
);

export default UploadImageItem;
